/**
 * Created by Carsten Hibbeler on 21.01.2016.
 * Copyright: xdot GmbH 2015
 */
import RemoteDataStore from './RemoteDataStore';
import ActionTypesCommon from '../constants/ActionTypesCommon';
import deepMerge from '../utils/deepMerge';
import { AppContext } from '../app-context';
import AsyncState from '../constants/AsyncState';

function findStoreForCQP(cqp) {
    const idx = AppContext.getStores().findIndex(s => s.containsRemoteData && s.containsRemoteData(cqp.sourceName, cqp.sourceClassName));
    return idx > -1 && AppContext.getStores()[ idx ];
}

function findStoreForCQPByClass(cqp) {
    const idx = AppContext.getStores().findIndex(s => s.className && s.className === cqp.sourceClassName);
    return idx > -1 && AppContext.getStores()[ idx ];
}

function findDispatchTokensForCQPs(cqps, curStore, fallbackToClassSearch) {
    let token = cqps.map(findStoreForCQP).filter(s => s && (s !== curStore)).map(s => s.getDispatchToken());
    if (fallbackToClassSearch && token.length === 0) {
        token = cqps.map(findStoreForCQPByClass).filter(s => s && (s !== curStore)).map(s => s.getDispatchToken());
    }
    return token;
}

function findDataForCQPs(cqp) {
    const store = findStoreForCQP(cqp),
        data = store && store.getRemoteData(cqp.sourceName);
    return data && (!cqp.sourceClassName || cqp.sourceClassName === data.className) && data;
}

function calculateDataUpdate(cqps, existingQuery) {
    let update = null;
    cqps.forEach(cqp => {
        const data = findDataForCQPs(cqp);

        //calculate current cqp value
        let v;
        if (data && !data._invalid && !data._loading && !data._error) {
            v = data.item[ cqp.sourceProp ];
        }
        if (!v && data && cqp.sourceProp === 'id') {
            //special handling for id
            v = data.query.id;
        }

        if (v && (existingQuery && existingQuery[ cqp.param ] === v)) {
            console.log('Unchanged calculateQueryParam', cqp, existingQuery, data);
        } else {
            update = update || { query: deepMerge({}, existingQuery) };
            if (v) {
                update.query[ cqp.param ] = v;
            } else if (!v && !data) {
                console.warn(`Cannot calculate calculateQueryParam! ${cqp.sourceName} is not yet initialized.`, cqp, data);
                update._error = `Cannot calculate calculateQueryParam! ${cqp.sourceName} is not yet initialized.`;
            } else if (!v && data && data._invalid) {
                console.warn(`Cannot calculate calculateQueryParam! ${cqp.sourceName} is invalid.`, cqp, data);
                update._error = `Cannot calculate calculateQueryParam! ${cqp.sourceName} is invalid.`;
            } else if (!v && data && data._loading) {
                console.warn(`Cannot calculate calculateQueryParam! ${cqp.sourceName} is still loading.`, cqp, data);
                update._error = `Cannot calculate calculateQueryParam! ${cqp.sourceName} is still loading.`;
            } else if (!v && data && data._error) {
                console.warn(`Cannot calculate calculateQueryParam! ${cqp.sourceName} is still erroneous.`, cqp, data);
                update._error = `Cannot calculate calculateQueryParam! ${cqp.sourceName} is still erroneous.`;
            } else {
                console.warn('Cannot calculate calculateQueryParam!', cqp, data);
                update._error = 'Cannot calculate calculateQueryParam!';
            }
        }

        if (update && v && (existingQuery && existingQuery[ cqp.param ] !== v)) {
            update.query.changesSince && (update.query.changesSince = undefined);
            update.query.start && (update.query.start = 0);
            update.query.end && (update.query.end = AppContext.DEFAULT_LIST_SIZE);
        }

    });

    return update;
}


/**
 * Parsing computed props from query
 * @param name data name
 * @param query
 * @returns {*|Array} of cqps
 */
function parseCqps(name, query) {
    /*
     query example:
     const linkedListQuery = {
        $byId: ClassNames.CriticalObject + '/' + this.DATA.SELECTED_CRITICAL_OBJECT + '/id',
        byClassName: ClassNames.CriticalObject,
        start: 0,
        end: AppContext.DEFAULT_LIST_SIZE
     };
     */
    return query && Object.keys(query).filter(p => p[ 0 ] === '$').map(p => {
        const s = query[ p ].split('/');
        return {
            name, // list name
            param: p.substring(1, p.length),
            sourceClassName: s.length > 2 && s[ 0 ], // optional
            sourceName: s[ s.length - 2 ], // referenced list
            sourceProp: s[ s.length - 1 ]
        };
    }) || [];
}


export default class ComputedQueryParamsDataStore extends RemoteDataStore {

    constructor(dispatcher) {
        super(dispatcher);

        //myCqpsBySourceName[this.DATA.SELECTED_FRB]=[this.DATA.FRB_DETAILS,this.DATA.FRB_MAP_SELECTED]
        this.myCqpsBySourceName = {};
    }

    doSetRemoteState(state, remote, forceCqps = {}) {
        const oldRemote = this.getRemoteState();
        Object.keys(remote).forEach(name => {
            const data = remote[ name ],
                oldData = oldRemote[ name ];

            if (oldData !== data) {
                forceCqps[ name ] = true;
                this.getMyCqpsSourceOf(name, data.className).forEach(cqp => forceCqps[ cqp.name ] = true);
            }

        });

        Object.keys(forceCqps).forEach(name => {
            const data = remote[ name ],
                cqps = parseCqps(name, data.query);

            this.cacheMyCqps(name, cqps);

            if (cqps.length > 0) {
                this.getDispatcher().waitFor(findDispatchTokensForCQPs(cqps, this));
                const dataUpdate = calculateDataUpdate(cqps, data.query);
                if (dataUpdate) {
                    remote[ name ] = RemoteDataStore.updateData(data, dataUpdate);
                }
            }
        });

        //Handle Removed
        Object.keys(oldRemote).filter(name => !remote[ name ]).forEach(name => {
            this.removeMyCqpsSourceOf(name);
        });

        return super.doSetRemoteState(state, remote);
    }


    getMyCqpsSourceOf(name, className) {
        return this.myCqpsBySourceName[ name ] &&
            this.myCqpsBySourceName[ name ].filter(cqp => !cqp.sourceClassName || cqp.sourceClassName === className) || [];
    }

    removeMyCqpsSourceOf(name) {
        Object.keys(this.myCqpsBySourceName).forEach(sourceName => {
            this.myCqpsBySourceName[ sourceName ] = this.myCqpsBySourceName[ sourceName ].filter(cqp => cqp.name !== name);
            if (this.myCqpsBySourceName[ sourceName ].length === 0) {
                delete this.myCqpsBySourceName[ sourceName ];
            }
        });
    }

    // cache own cpqs
    // update already existing cached cqps
    cacheMyCqps(name, cqps) {
        // filter for cqps where cqp.name !== name
        Object.keys(this.myCqpsBySourceName).forEach(sourceName => {
            this.myCqpsBySourceName[ sourceName ] = this.myCqpsBySourceName[ sourceName ].filter(cqp => cqp.name !== name);
        });
        // add/update
        cqps.forEach((cqp) => {
            (this.myCqpsBySourceName[ cqp.sourceName ] || (this.myCqpsBySourceName[ cqp.sourceName ] = [])).push(cqp);
        });
    }


    reduce(state, action) {
        let name, className;

        switch (action.type) {

            case ActionTypesCommon.DATA_LIST_INIT:
                ({ name, className } = action.meta);

                if (this.className !== className) {
                    //nothing to do
                }

                break;
            case ActionTypesCommon.DATA_ASYNC:
                ({ name, className } = action.meta);
                if (action.meta.asyncState === AsyncState.SUCCESS) {
                    state = this.handleCqpUpdates(name, className, action, state);
                }
                break;


            case ActionTypesCommon.DATA_ITEM_INIT: {
                ({ name, className } = action.meta);

                //   if (this.className != className) { //should always be true
                //get all of my remotedata's with queries that contain cqp with the source 'name' and 'className'
                state = this.handleCqpUpdates(name, className, action, state);
                //     }
            }
        }

        return super.reduce(state, action);
    }

    handleCqpUpdates(name, className, action, state) {
        const cqps = this.getMyCqpsSourceOf(name, className);

        if (cqps && cqps.length > 0) {
            //    let remote;
            this.getDispatcher().waitFor(findDispatchTokensForCQPs(cqps, this, true));
            const cqpsByName = cqps.reduce((byName, cqp) => {
                byName[ cqp.name ] = byName[ cqp.name ] || [];
                byName[ cqp.name ].push(cqp);
                return byName;
            }, {});

            console.log(this.constructor.name, 'handle', action, state.remote, '');
            state = this.doSetRemoteState(state, state.remote, cqpsByName);
        }
        return state;
    }
}




// WEBPACK FOOTER //
// ./js/stores/ComputedQueryParamsDataStore.js